import React, {useState, useEffect} from "react";

import {wod1Scale, wod2Scale, wod31Scale, wod32Scale, wod1RX, wod2RX, wod31RX, wod32RX} from "./wods";
import {Wod, AmrapResult, TimeCapResult, WeightResult, WodType, MaxTimeResult} from "./types";
import {computeFinal} from "./overall";

const initialWods = [wod1Scale, wod1RX, wod2Scale, wod2RX, wod31Scale, wod31RX, wod32Scale, wod32RX];

const Results: React.FC = () => {
    const [wods, setWods] = useState<Wod[]>(initialWods);
    const [expandedWods, setExpandedWods] = useState<Set<number>>(new Set([0])); // First WOD expanded by default

    // Load saved results from localStorage on component mount
    useEffect(() => {
        try {
            const savedWodsString = localStorage.getItem('wodResults');
            if (savedWodsString) {
                const savedWods: { [key: string]: Wod } = JSON.parse(savedWodsString);

                // Merge saved results with initial wods
                const updatedWods = initialWods.map(wod => {
                    if (savedWods[wod.name]) {
                        return savedWods[wod.name];
                    }
                    return wod;
                });

                setWods(updatedWods);
                console.log("Loaded saved WOD results from localStorage");
            }
        } catch (error) {
            console.error("Error loading saved WODs from localStorage:", error);
        }
    }, []);

    const toggleWod = (index: number) => {
        const newExpanded = new Set(expandedWods);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedWods(newExpanded);
    };

    const handleResultChange = (wodIndex: number, teamIndex: number, value: string) => {
        const updatedWods = [...wods];
        const currentWod = updatedWods[wodIndex];

        updatedWods[wodIndex] = {
            ...currentWod,
            results: currentWod.results.map((teamResult, idx) => {
                if (idx === teamIndex) {
                    // Parse the result based on WOD type
                    let parsedResult = teamResult.result;

                    if (currentWod.type === 0) { // AMRAP
                        const reps = parseInt(value) || 0;
                        parsedResult = { reps };
                    } else if (currentWod.type === 2) { // WEIGHT
                        const weight = parseFloat(value) || 0;
                        parsedResult = { weight };
                    }

                    return { ...teamResult, result: parsedResult };
                }
                return teamResult;
            })
        };
        setWods(updatedWods);
    };

    const handleTimeCapChange = (wodIndex: number, teamIndex: number, field: 'mins' | 'seconds' | 'reps' | 'cap', value: number | boolean) => {
        const updatedWods = [...wods];
        const currentWod = updatedWods[wodIndex];

        updatedWods[wodIndex] = {
            ...currentWod,
            results: currentWod.results.map((teamResult, idx) => {
                if (idx === teamIndex) {
                    const currentResult = teamResult.result as TimeCapResult;

                    if (field === 'cap') {
                        // When cap is toggled
                        if (value) {
                            return {
                                ...teamResult,
                                result: { cap: true, mins: 0, seconds: 0, reps: 0 }
                            };
                        } else {
                            return {
                                ...teamResult,
                                result: { ...currentResult, cap: false }
                            };
                        }
                    } else {
                        // Update specific field
                        return {
                            ...teamResult,
                            result: {
                                ...currentResult,
                                [field]: value
                            }
                        };
                    }
                }
                return teamResult;
            })
        };
        setWods(updatedWods);
    };

    const handleMaxTimeChange = (wodIndex: number, teamIndex: number, field: 'mins' | 'seconds', value: number) => {
        const updatedWods = [...wods];
        const currentWod = updatedWods[wodIndex];

        updatedWods[wodIndex] = {
            ...currentWod,
            results: currentWod.results.map((teamResult, idx) => {
                if (idx === teamIndex) {
                    const currentResult = teamResult.result as MaxTimeResult;
                    return {
                        ...teamResult,
                        result: {
                            ...currentResult,
                            [field]: value
                        }
                    };
                }
                return teamResult;
            })
        };
        setWods(updatedWods);
    };

    const handleSaveWod = (wod: Wod) => {
        try {
            // Get existing saved WODs from localStorage
            const savedWodsString = localStorage.getItem('wodResults');
            const savedWods: { [key: string]: Wod } = savedWodsString ? JSON.parse(savedWodsString) : {};

            // Save the current WOD by name
            savedWods[wod.name] = wod;

            // Store back to localStorage
            localStorage.setItem('wodResults', JSON.stringify(savedWods));

            console.log("Saved WOD to localStorage:", wod);
            alert(`Successfully saved results for ${wod.name}!`);
        } catch (error) {
            console.error("Error saving WOD to localStorage:", error);
            alert(`Error saving results for ${wod.name}. Please try again.`);
        }
    };

    const handleClearWod = (wodIndex: number) => {
        const wodName = wods[wodIndex].name;
        const isFinalEvent = wodName.includes("Final Event");

        const confirmed = window.confirm(
            isFinalEvent
                ? `Are you sure you want to remove ${wodName}? This will delete the final event.`
                : `Are you sure you want to clear all results for ${wodName}? This will reset to initial values.`
        );

        if (confirmed) {
            try {
                // Check if this is a final event
                if (isFinalEvent) {
                    // Remove final event from state
                    const updatedWods = wods.filter((_, index) => index !== wodIndex);
                    setWods(updatedWods);

                    // Remove from localStorage
                    const savedWodsString = localStorage.getItem('wodResults');
                    const savedWods: { [key: string]: Wod } = savedWodsString ? JSON.parse(savedWodsString) : {};
                    delete savedWods[wodName];
                    localStorage.setItem('wodResults', JSON.stringify(savedWods));

                    // Update expanded set to account for removed index
                    const newExpanded = new Set<number>();
                    expandedWods.forEach(idx => {
                        if (idx < wodIndex) {
                            newExpanded.add(idx);
                        } else if (idx > wodIndex) {
                            newExpanded.add(idx - 1);
                        }
                    });
                    setExpandedWods(newExpanded);

                    console.log("Removed final event:", wodName);
                    alert(`Successfully removed ${wodName}!`);
                } else {
                    // Find the initial WOD for regular events
                    const initialWod = initialWods.find(w => w.name === wodName);

                    if (initialWod) {
                        // Update state with initial WOD
                        const updatedWods = [...wods];
                        updatedWods[wodIndex] = { ...initialWod };
                        setWods(updatedWods);

                        // Remove from localStorage
                        const savedWodsString = localStorage.getItem('wodResults');
                        const savedWods: { [key: string]: Wod } = savedWodsString ? JSON.parse(savedWodsString) : {};
                        delete savedWods[wodName];
                        localStorage.setItem('wodResults', JSON.stringify(savedWods));

                        console.log("Cleared WOD results:", wodName);
                        alert(`Successfully cleared results for ${wodName}!`);
                    }
                }
            } catch (error) {
                console.error("Error clearing WOD results:", error);
                alert(`Error clearing results for ${wodName}. Please try again.`);
            }
        }
    };

    const handleGenerateFinals = () => {
        try {
            const [scaleFinal, rxFinal] = computeFinal();

            // Check if final WODs already exist in wods array
            const scaleIndex = wods.findIndex(w => w.name === scaleFinal.name && w.category === scaleFinal.category);
            const rxIndex = wods.findIndex(w => w.name === rxFinal.name && w.category === rxFinal.category);

            const updatedWods = [...wods];
            let message = '';

            // Replace or add Scale Final
            if (scaleIndex !== -1) {
                updatedWods[scaleIndex] = scaleFinal;
                message += 'Scale Final updated. ';
            } else {
                updatedWods.push(scaleFinal);
                message += 'Scale Final added. ';
            }

            // Replace or add RX Final
            if (rxIndex !== -1) {
                updatedWods[rxIndex] = rxFinal;
                message += 'RX Final updated.';
            } else {
                updatedWods.push(rxFinal);
                message += 'RX Final added.';
            }

            setWods(updatedWods);

            // Expand the final WODs
            const newExpanded = new Set(expandedWods);
            if (scaleIndex !== -1) {
                newExpanded.add(scaleIndex);
            } else {
                newExpanded.add(updatedWods.length - 2);
            }
            if (rxIndex !== -1) {
                newExpanded.add(rxIndex);
            } else {
                newExpanded.add(updatedWods.length - 1);
            }
            setExpandedWods(newExpanded);

            console.log("Generated final events:", scaleFinal, rxFinal);
            alert(`Final events generated successfully! ${message}\n\nTop 5 teams from each category have been selected. You can now enter their final event results below.`);
        } catch (error) {
            console.error("Error generating final events:", error);
            alert("Error generating final events. Please make sure you have saved WOD results first.");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">WOD Results Administration</h1>

                    {/* Generate Finals Button */}
                    <button
                        onClick={handleGenerateFinals}
                        className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold rounded-lg hover:from-emerald-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                        🏆 Generate Final Events
                    </button>
                </div>

                <div className="space-y-8">
                    {wods.map((wod, i) => {
                        const isExpanded = expandedWods.has(i);
                        return (
                            <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden">
                                {/* WOD Header - Clickable */}
                                <button
                                    onClick={() => toggleWod(i)}
                                    className="w-full px-6 py-4 flex items-center justify-between transition-all bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black"
                                >
                                    <div className="text-left">
                                        <h2 className="text-2xl font-bold text-white">{wod.name}</h2>
                                        <span
                                            className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${
                                                wod.category === 0
                                                    ? 'bg-gray-400 text-gray-900'
                                                    : 'bg-orange-500 text-white'
                                            }`}
                                        >
                                            {wod.category === 0 ? "SCALE" : "RX"}
                                        </span>
                                    </div>
                                    <div className="ml-4">
                                        <svg
                                            className={`w-6 h-6 text-white transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </div>
                                </button>

                                {/* Teams/Participants List - Collapsible */}
                                {isExpanded && (
                                    <div className="p-6">
                                        <div className="space-y-4">
                                            {wod.results.map((t, j) => (
                                                <div
                                                    key={`${i}-${j}`}
                                                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                                                >
                                                    {/* Team Name */}
                                                    <div className="flex-1">
                                                        <label className="text-sm font-medium text-gray-500 mb-1 block">
                                                            Team #{j + 1}
                                                        </label>
                                                        <div className="text-lg font-semibold text-gray-900">
                                                            {t.team.athlete1} & {t.team.athlete2}
                                                        </div>
                                                    </div>

                                                    {/* Result Input */}
                                                    <div className="flex-1">
                                                        {wod.type === WodType.TIMECAP ? (
                                                            <div className="space-y-3">
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Result
                                                                </label>

                                                                {/* Cap Checkbox */}
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <input
                                                                        id={`cap-${i}-${j}`}
                                                                        type="checkbox"
                                                                        checked={(t.result as TimeCapResult).cap || false}
                                                                        onChange={(e) => handleTimeCapChange(i, j, 'cap', e.target.checked)}
                                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                                    />
                                                                    <label htmlFor={`cap-${i}-${j}`} className="text-sm text-gray-700 font-medium">
                                                                        CAP
                                                                    </label>
                                                                </div>

                                                                {/* Time Inputs */}
                                                                <div className="flex gap-2 items-center">
                                                                    <div className="flex-1">
                                                                        <label htmlFor={`mins-${i}-${j}`} className="block text-xs text-gray-600 mb-1">
                                                                            Minutes
                                                                        </label>
                                                                        <input
                                                                            id={`mins-${i}-${j}`}
                                                                            type="number"
                                                                            min="0"
                                                                            value={(t.result as TimeCapResult).mins || 0}
                                                                            onChange={(e) => handleTimeCapChange(i, j, 'mins', parseInt(e.target.value) || 0)}
                                                                            disabled={(t.result as TimeCapResult).cap}
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                                        />
                                                                    </div>
                                                                    <span className="text-gray-500 mt-5">:</span>
                                                                    <div className="flex-1">
                                                                        <label htmlFor={`seconds-${i}-${j}`} className="block text-xs text-gray-600 mb-1">
                                                                            Seconds
                                                                        </label>
                                                                        <input
                                                                            id={`seconds-${i}-${j}`}
                                                                            type="number"
                                                                            min="0"
                                                                            max="59"
                                                                            value={(t.result as TimeCapResult).seconds || 0}
                                                                            onChange={(e) => handleTimeCapChange(i, j, 'seconds', parseInt(e.target.value) || 0)}
                                                                            disabled={(t.result as TimeCapResult).cap}
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* Reps Input - shown when CAP is checked */}
                                                                {(t.result as TimeCapResult).cap && (
                                                                    <div>
                                                                        <label htmlFor={`reps-${i}-${j}`} className="block text-xs text-gray-600 mb-1">
                                                                            Reps at CAP
                                                                        </label>
                                                                        <input
                                                                            id={`reps-${i}-${j}`}
                                                                            type="number"
                                                                            min="0"
                                                                            value={(t.result as TimeCapResult).reps || 0}
                                                                            onChange={(e) => handleTimeCapChange(i, j, 'reps', parseInt(e.target.value) || 0)}
                                                                            placeholder="Enter reps..."
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : wod.type === WodType.MAXTIME ? (
                                                            <div className="space-y-3">
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Result (Max Time)
                                                                </label>

                                                                {/* Time Inputs */}
                                                                <div className="flex gap-2 items-center">
                                                                    <div className="flex-1">
                                                                        <label htmlFor={`maxtime-mins-${i}-${j}`} className="block text-xs text-gray-600 mb-1">
                                                                            Minutes
                                                                        </label>
                                                                        <input
                                                                            id={`maxtime-mins-${i}-${j}`}
                                                                            type="number"
                                                                            min="0"
                                                                            value={(t.result as MaxTimeResult).mins || 0}
                                                                            onChange={(e) => handleMaxTimeChange(i, j, 'mins', parseInt(e.target.value) || 0)}
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                        />
                                                                    </div>
                                                                    <span className="text-gray-500 mt-5">:</span>
                                                                    <div className="flex-1">
                                                                        <label htmlFor={`maxtime-seconds-${i}-${j}`} className="block text-xs text-gray-600 mb-1">
                                                                            Seconds
                                                                        </label>
                                                                        <input
                                                                            id={`maxtime-seconds-${i}-${j}`}
                                                                            type="number"
                                                                            min="0"
                                                                            max="59"
                                                                            value={(t.result as MaxTimeResult).seconds || 0}
                                                                            onChange={(e) => handleMaxTimeChange(i, j, 'seconds', parseInt(e.target.value) || 0)}
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <label
                                                                    htmlFor={`result-${i}-${j}`}
                                                                    className="block text-sm font-medium text-gray-700 mb-2"
                                                                >
                                                                    Result {wod.type === WodType.AMRAP && '(reps)'}
                                                                    {wod.type === WodType.WEIGHT && '(kg)'}
                                                                </label>
                                                                <input
                                                                    id={`result-${i}-${j}`}
                                                                    type="number"
                                                                    min="0"
                                                                    value={
                                                                        wod.type === WodType.AMRAP
                                                                            ? (t.result as AmrapResult).reps || 0
                                                                            : (t.result as WeightResult).weight || 0
                                                                    }
                                                                    onChange={(e) => handleResultChange(i, j, e.target.value)}
                                                                    placeholder={
                                                                        wod.type === WodType.AMRAP ? "Enter reps..." :
                                                                        "Enter weight..."
                                                                    }
                                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Points Display */}
                                                    <div className="w-24 text-center">
                                                        <label className="text-sm font-medium text-gray-500 mb-1 block">
                                                            Points
                                                        </label>
                                                        <div className="text-lg font-bold text-blue-600">
                                                            {t.points}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Save and Clear Buttons */}
                                        <div className="mt-6 flex justify-end gap-3">
                                            <button
                                                onClick={() => handleClearWod(i)}
                                                className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors shadow-md hover:shadow-lg"
                                            >
                                                {wod.name.includes("Final Event") ? `Remove ${wod.name}` : `Clear ${wod.name} Results`}
                                            </button>
                                            <button
                                                onClick={() => handleSaveWod(wods[i])}
                                                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors shadow-md hover:shadow-lg"
                                            >
                                                Save {wod.name} Results
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default Results;